import React, { useState } from 'react';
import PropTypes from 'prop-types';

import CreateWizardLayout from '../../../../../components/CreateWizardLayout';
import Alert from '../../../../../../../components/global/Alert';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import Button from '../../../../../../../components/global/Button';
import usePhoneAuth from '../../../../../../../components/auth/hooks/usePhoneAuth';
import useGoogleRecaptcha from '../../../../../../../services/recaptcha/useGoogleRecaptcha';
import CustomControl from '../../../../../../../components/global/CustomControl';
import PinField from '../../../../../../../components/global/PinField';
import { getStepArrayFromVariant } from '../../util';
import { FREEMIUM_EVENT_STEPS } from '../../constants';
import useEmailPinAuth from '../../../../../../../components/auth/hooks/useEmailPinAuth';

const propTypes = {
  values: PropTypes.shape({
    code: PropTypes.string,
  }).isRequired,
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  stepProps: PropTypes.shape({
    setActiveStepIndex: PropTypes.func,
  }),
};
const FreemiumEventCreatePhoneVerificationStep = ({
  values,
  errors,
  handleChange,
  steps,
  stepProps,
  variant,
}) => {
  const openAlert = useOpenAlert();
  const [loading, setLoading] = useState(false);
  const { requireHumanInput, blockLogin, onResendPhoneAuthCode } =
    usePhoneAuth();
  const { onEmailAuthenticationRequest } = useEmailPinAuth();
  const { onTokenRequest } = useGoogleRecaptcha();
  const { setActiveStepIndex } = stepProps;

  const onPinCodeResend = async () => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      const token = await onTokenRequest({ action: 'login' });
      await onResendPhoneAuthCode(
        values.account_phone_number,
        true,
        token,
        values.force_allow_action,
      );
    } catch (error) {
      openAlert({
        message: 'There was an error sending the PIN.',
        info: error?.request_id ?? getErrorString(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredSteps = getStepArrayFromVariant(variant, steps);

  const emailPinStepIndex = filteredSteps.findIndex(
    filteredStep =>
      filteredStep.key === FREEMIUM_EVENT_STEPS.EMAIL_PIN_VERIFICATION,
  );

  const errorList = Object.values(errors);

  return (
    <CreateWizardLayout.Wrapper>
      <CreateWizardLayout.Content size="lg">
        <div className="row">
          <div className="col-12">
            <div className="mx-auto freemium-phone-verification-pin-input">
              <PinField
                autofocus
                className="registration-pin-field"
                onComplete={value => {
                  handleChange({
                    target: {
                      name: 'account_code',
                      value,
                    },
                  });
                }}
                onChange={value => {
                  handleChange({
                    target: {
                      name: 'account_code',
                      value,
                    },
                  });
                }}
              />
              {requireHumanInput && (
                <div className="mb-1">
                  <CustomControl
                    type="checkbox"
                    label="I'm not a robot"
                    checked={values.force_allow_action}
                    onChange={() =>
                      handleChange({
                        target: {
                          name: 'force_allow_action',
                          value: !values.force_allow_action,
                        },
                      })
                    }
                  />
                </div>
              )}
              <Button
                btnStyle="link"
                onClick={() => onPinCodeResend()}
                disabled={
                  loading ||
                  blockLogin ||
                  (requireHumanInput && !values.force_allow_action)
                }
              >
                {loading ? 'Resending...' : 'Resend code'}
              </Button>
            </div>

            {emailPinStepIndex && (
              <div className="text-center mt-2 text-sm">
                Can&apos;t receive an SMS?&nbsp;
                <Button
                  btnStyle="link"
                  btnSize="sm"
                  onClick={e => {
                    e.preventDefault();
                    onEmailAuthenticationRequest({
                      emailAddress: values.account_email_address,
                    });
                    setActiveStepIndex(emailPinStepIndex);
                  }}
                >
                  Receive it via email
                </Button>
              </div>
            )}
          </div>
        </div>
        {errorList.length > 0 && (
          <Alert type="danger" className="mt-1 text-normal">
            <ul className="mb-0">
              {errorList.map(error => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
      </CreateWizardLayout.Content>
    </CreateWizardLayout.Wrapper>
  );
};

FreemiumEventCreatePhoneVerificationStep.propTypes = propTypes;

export default FreemiumEventCreatePhoneVerificationStep;
